import type { ItemDetailDto, ItemDto } from '@bnn/shared';
import { useEffect, useRef, useState } from 'react';
import { getItem, searchItems } from '../count/master';
import { errMsg } from '../lib/useApi';
import { Alert, Badge, Input, Spinner, useDebounced } from './ui';

/**
 * Search items by code or name (several words = AND). Non-countable items are shown but disabled,
 * so a counter understands why an item cannot be counted (Requirements §5.4).
 */
export function ItemPicker({ onPick, autoFocus = true, placeholder }: { onPick: (item: ItemDto) => void; autoFocus?: boolean; placeholder?: string }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 200);
  const [rows, setRows] = useState<ItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dq) {
      setRows([]);
      return;
    }
    let alive = true;
    setLoading(true);
    // Offline-capable: searches the master cached on the device (falls back to the API when empty).
    searchItems(dq, 30)
      .then((r) => alive && (setRows(r), setError('')))
      .catch((e) => alive && setError(errMsg(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [dq]);

  return (
    <div>
      <Input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder ?? 'พิมพ์รหัสหรือชื่อสินค้า'}
        autoFocus={autoFocus}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const first = rows.find((r) => r.countable);
            if (first) onPick(first);
          } else if (e.key === 'ArrowDown') {
            (listRef.current?.querySelector('button:not(:disabled)') as HTMLButtonElement | null)?.focus();
          }
        }}
      />
      {error && (
        <div className="mt-2">
          <Alert>{error}</Alert>
        </div>
      )}
      <div ref={listRef} className="mt-2 max-h-[50vh] divide-y divide-line overflow-auto">
        {loading && rows.length === 0 && (
          <div className="py-4 text-center text-muted">
            <Spinner />
          </div>
        )}
        {!loading && dq && rows.length === 0 && <p className="py-4 text-center text-muted">ไม่พบสินค้า</p>}
        {rows.map((i) => (
          <button
            key={i.item_code}
            type="button"
            disabled={!i.countable}
            onClick={() => onPick(i)}
            className="block w-full px-2 py-2.5 text-left hover:bg-surface focus:bg-surface focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="font-semibold">{i.item_code}</span> {i.item_name}
            <span className="ml-1 text-sm text-muted">
              · {i.inventory_uom} · {i.item_group}
            </span>{' '}
            {!i.countable ? <Badge>ไม่ใช่สินค้าคงคลัง</Badge> : !i.is_active && <Badge tone="warn">Inactive ใน SAP</Badge>}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Load an item's countable units (with factors) and barcodes. */
export const fetchItemDetail = async (code: string): Promise<ItemDetailDto> => {
  const d = await getItem(code);
  if (!d) throw new Error(`ไม่พบสินค้า ${code}`);
  return d;
};

/** Unit dropdown options: units the item actually uses first (inventory + preferred), then the rest of the group. */
export function UomOptions({ item }: { item: ItemDetailDto }) {
  const label = (u: ItemDetailDto['uoms'][number]) => (u.is_inventory ? `${u.uom_code} (Inventory)` : `${u.uom_code} = ${u.factor} ${item.inventory_uom}`);
  const main = item.uoms.filter((u) => u.is_inventory || u.preferred);
  const rest = item.uoms.filter((u) => !u.is_inventory && !u.preferred);
  return (
    <>
      <optgroup label="หน่วยที่สินค้านี้ใช้">
        {main.map((u) => (
          <option key={u.uom_code} value={u.uom_code}>
            {label(u)}
          </option>
        ))}
      </optgroup>
      {rest.length > 0 && (
        <optgroup label={`หน่วยอื่นในกลุ่ม ${item.uom_group_code ?? ''}`}>
          {rest.map((u) => (
            <option key={u.uom_code} value={u.uom_code}>
              {label(u)}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}
